import { Accordion, AccordionSummary, AccordionDetails, Box, Stack, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import MDTypography from 'components/MDTypography';

export default function ImportConfigurationPreview({
  assistants = [],
  linkedEntities = {},
  prompts = [],
  scripts = [],
  selectedAssistants,
  setSelectedAssistants,
  selectedLinkedEntities,
  setSelectedLinkedEntities,
  selectedPrompts,
  setSelectedPrompts,
  selectedScripts,
  setSelectedScripts,
  expandedAccordions,
  onAccordionChange
}) {
  const { formatMessage: msg } = useIntl();

  const handleAssistantSelectionChange = (newSelection) => {
    setSelectedAssistants(newSelection);

    const newSelectedLinkedEntities = {};
    newSelection.forEach((assistantId) => {
      const assistant = assistants.find((a) => a.id === assistantId);

      if (assistant?.dependencies) {
        Object.entries(assistant.dependencies).forEach(([entityType, entityIds]) => {
          if (!newSelectedLinkedEntities[entityType]) {
            newSelectedLinkedEntities[entityType] = [];
          }

          entityIds.forEach((entityId) => {
            const fullId = entityType === 'linkedScripts' ? entityId : `${entityType}_${entityId}`;
            if (!newSelectedLinkedEntities[entityType].includes(fullId)) {
              newSelectedLinkedEntities[entityType].push(fullId);
            }
          });
        });
      }
    });

    setSelectedLinkedEntities(newSelectedLinkedEntities);
  };

  const getConflictColumn = (entityTypeKey) => ({
    field: 'alreadyExist',
    headerName: msg({ id: 'page.import.table.column.conflict' }),
    width: 100,
    renderCell: (params) => {
      if (params.value === true) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Tooltip
              title={msg(
                { id: 'page.import.tooltip.alreadyExists' },
                { entity: msg({ id: entityTypeKey }) }
              )}
            >
              <FmdBadIcon color="warning" fontSize="small" />
            </Tooltip>
          </Box>
        );
      }
      return null;
    },
    sortable: false,
    filterable: false
  });

  const assistantColumns = [
    {
      field: 'code',
      headerName: msg({ id: 'page.assistants.table.column.code' }),
      minWidth: 250
    },
    {
      field: 'label',
      headerName: msg({ id: 'page.assistants.table.column.label' }),
      minWidth: 250
    },
    {
      field: 'creationDate',
      headerName: msg({ id: 'page.assistants.table.column.creationDate' }),
      renderCell: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
      minWidth: 150
    },
    getConflictColumn('page.import.entity.assistant')
  ];

  const promptColumns = [
    {
      field: 'id',
      headerName: msg({ id: 'page.prompts.table.column.id' }),
      width: 50
    },
    {
      field: 'code',
      headerName: msg({ id: 'page.prompts.table.column.code' }),
      minWidth: 350
    },
    {
      field: 'description',
      headerName: msg({ id: 'page.prompts.table.column.description' }),
      minWidth: 350
    },
    getConflictColumn('page.import.entity.prompt')
  ];

  const scriptColumns = [
    {
      field: 'code',
      headerName: msg({ id: 'page.scripts.table.column.code' }),
      minWidth: 300
    },
    {
      field: 'name',
      headerName: msg({ id: 'page.scripts.table.column.name' }),
      minWidth: 300,
      valueGetter: (value, row) => row.code?.toLowerCase() || ''
    },
    getConflictColumn('page.import.entity.script')
  ];

  const getLinkedEntityColumns = (entityType) => {
    const entityTypeKeyMap = {
      accounts: 'page.import.entity.account',
      knowledgeBases: 'page.import.entity.knowledgeBase',
      models: 'page.import.entity.model',
      providers: 'page.import.entity.provider',
      linkedScripts: 'page.import.entity.linkedScript'
    };

    // Entity types that should not have conflict column
    const noConflictTypes = ['accounts', 'knowledgeBases', 'models', 'providers'];

    const columns = [
      {
        field: 'id',
        headerName: msg({ id: 'page.linkedEntities.table.column.id' }),
        width: 50
      },
      {
        field: 'code',
        headerName: msg({ id: 'page.linkedEntities.table.column.code' }),
        minWidth: 350
      },
      {
        field: 'label',
        headerName: msg({ id: 'page.linkedEntities.table.column.label' }),
        minWidth: 350
      }
    ];

    // Add conflict column only for entity types not in the exclusion list
    if (!noConflictTypes.includes(entityType)) {
      columns.push(getConflictColumn(entityTypeKeyMap[entityType] || 'page.import.entity.entity'));
    }

    return columns;
  };

  const renderAccordion = (
    title,
    data,
    columns,
    selectionModel,
    onSelectionChange,
    getRowIdFunc,
    accordionKey
  ) => {
    if (!data || data.length === 0) return null;

    return (
      <Accordion
        disableGutters
        square
        expanded={expandedAccordions[accordionKey] || false}
        onChange={onAccordionChange(accordionKey)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="large" />}
          aria-controls={`${accordionKey}-content`}
          id={`${accordionKey}-header`}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: '100%', justifyContent: 'space-between' }}
          >
            <MDTypography variant="h6">{title}</MDTypography>
            {selectionModel && selectionModel.length > 0 ? (
              <MDTypography variant="body2" color="text">
                {msg(
                  { id: 'page.exportConfiguration.selection.count' },
                  { count: selectionModel.length }
                )}
              </MDTypography>
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={data}
              columns={columns}
              getRowId={getRowIdFunc}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={onSelectionChange}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                density: 'compact',
                sorting: {
                  sortModel: [{ field: 'code', sort: 'asc' }]
                }
              }}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {renderAccordion(
        msg({ id: 'page.exportConfiguration.tab.assistants.title' }),
        assistants,
        assistantColumns,
        selectedAssistants,
        handleAssistantSelectionChange,
        (row) => row.id,
        'assistants'
      )}

      {Object.entries(linkedEntities).map(([entityType, entities]) => {
        if (!entities || entities.length === 0) return null;

        return (
          <div key={entityType}>
            {renderAccordion(
              msg({ id: `page.linkedEntities.type.${entityType}` }, { defaultMessage: entityType }),
              entities,
              getLinkedEntityColumns(entityType),
              selectedLinkedEntities[entityType] || [],
              (newSelection) => {
                setSelectedLinkedEntities({
                  ...selectedLinkedEntities,
                  [entityType]: newSelection
                });
              },
              (row) =>
                entityType === 'linkedScripts' ? row.code : `${entityType}_${row.id ?? row.code}`,
              `linkedEntity_${entityType}`
            )}
          </div>
        );
      })}

      {renderAccordion(
        msg({ id: 'page.exportConfiguration.tab.prompts.title' }),
        prompts,
        promptColumns,
        selectedPrompts,
        setSelectedPrompts,
        (row) => row.id,
        'prompts'
      )}

      {renderAccordion(
        msg({ id: 'page.exportConfiguration.tab.scripts.title' }),
        scripts,
        scriptColumns,
        selectedScripts,
        setSelectedScripts,
        (row) => row.code,
        'scripts'
      )}
    </Box>
  );
}

ImportConfigurationPreview.propTypes = {
  assistants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      code: PropTypes.string,
      label: PropTypes.string,
      isPublic: PropTypes.bool,
      creationDate: PropTypes.string,
      alreadyExists: PropTypes.bool,
      dependencies: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
      )
    })
  ),
  linkedEntities: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        label: PropTypes.string,
        alreadyExists: PropTypes.bool
      })
    )
  ),
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      code: PropTypes.string,
      description: PropTypes.string,
      alreadyExists: PropTypes.bool
    })
  ),
  scripts: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      alreadyExists: PropTypes.bool
    })
  ),
  selectedAssistants: PropTypes.array.isRequired,
  setSelectedAssistants: PropTypes.func.isRequired,
  selectedLinkedEntities: PropTypes.object.isRequired,
  setSelectedLinkedEntities: PropTypes.func.isRequired,
  selectedPrompts: PropTypes.array.isRequired,
  setSelectedPrompts: PropTypes.func.isRequired,
  selectedScripts: PropTypes.array.isRequired,
  setSelectedScripts: PropTypes.func.isRequired,
  expandedAccordions: PropTypes.object.isRequired,
  onAccordionChange: PropTypes.func.isRequired
};
